import React, { useEffect, useState } from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, Link } from "react-router-dom";

import PersonIcon from "@mui/icons-material/Person";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import KeyIcon from "@mui/icons-material/Key";

import { setUser, showToast } from "../../store/actions";
import AuthService from "../../service/auth";
import Loading from "../Loading";
import PasswordInput from "../Common/PasswordInput";

export default function SignIn() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);

  const location = useLocation();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    if (queryParams.has("message") && queryParams.get("message")) {
      const message = queryParams.get("message");
      dispatch(
        showToast({
          message,
          type: "error",
        })
      );

      queryParams.delete("message");
      navigate("/signin");
    }
  }, [navigate, dispatch, location.search]);

  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [authIDPList, setAuthIDPList] = useState([]);
  const [code, setCode] = useState("");
  const [codeRequired, setCodeRequired] = useState(false);
  const [loading, setLoading] = useState(false);

  //Check of user is Logged in then redirecting to Account List
  useEffect(() => {
    if (user.currentUser) navigate("/control-panel");
    else {
      const getAuthUser = async () => {
        const authService = new AuthService();
        const response = await authService.getAuthUser();

        if (response.success) {
          dispatch(setUser(response.data));
          navigate("/control-panel");
        } else {
          dispatch(setUser(null));
          loadIDPList();
        }
      };

      getAuthUser();
    }

    return () => {
      setLoginId("");
      setPassword("");
    };
  }, [user.currentUser, navigate, dispatch]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    setLoading(true);
    const authService = new AuthService();
    const response = await authService.login(loginId, password, code);

    if (response.success && response.data) {
      dispatch(setUser(response.data.user));

      navigate("/control-panel");

      setLoginId("");
      setPassword("");
      setCode("");
      setLoading(false);
    } else {
      setLoading(false);
      if (response.codeRequired) setCodeRequired(true);
    }
  };

  const loadIDPList = async () => {
    setLoading(true);

    const authService = new AuthService();
    const result = await authService.getIDPListForAuth();

    if (result.success && result.data && Object.keys(result.data).length > 0) {
      setAuthIDPList(result.data);
    }

    setLoading(false);
  };

  if (user.isLoading) return <Loading />;

  const handleAuthClick = (IDP_id) => {
    const apiURL = (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL : "") + "/api/v1";

    window.open(`${apiURL}/authProvider/login/idp/${IDP_id}`, "_self");
  };

  const LoadIDPList = () => {
    return (
      <Box sx={{ p: 0, m: 0, width: "100%", mt: 1 }}>
        {authIDPList.map((row, i) => (
          <Button
            data-id={"auth" + row.Name.replace(/\s/g, "") + "Button"}
            key={i}
            fullWidth
            variant="outlined"
            color="info"
            sx={{ m: "8px 0", textTransform: "none" }}
            onClick={() => handleAuthClick(row.IDP_id)}
          >
            {`Sign in with ${row.Name.toUpperCase()}`}
          </Button>
        ))}
        {authIDPList.length ? <Divider sx={{ mt: "8px" }}>or</Divider> : <></>}
      </Box>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <AppBar
        sx={{
          color: "black",
          backgroundColor: "white",
          boxShadow: "0px 3px 40px #4343430A",
        }}
        position="absolute"
      >
        <Toolbar>
          <Typography component="h1" variant="h6" color="inherit" noWrap sx={{ flexGrow: 1 }}>
            Vortex Control Panel
          </Typography>
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Container component="main" maxWidth="xs">
          <CssBaseline />
          <Box
            sx={{
              marginTop: 10,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              overflowY: "auto",
              justifyContent: "center",
            }}
          >
            <Avatar sx={{ m: 1, bgcolor: "primary.main" }} data-id="authVortexLogo">
              <img src="/images/vortex_white.png" alt="vortex" style={{ width: "20px" }} />
            </Avatar>
            <Typography component="h1" variant="h5" data-id="authSignInText">
              Sign in
            </Typography>

            <LoadIDPList />
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                margin="normal"
                autoComplete="username"
                onChange={(e) => setLoginId(e.target.value)}
                required
                fullWidth
                label="Login ID"
                data-id="authLoginId"
                autoFocus
                InputProps={{
                  endAdornment: <PersonIcon color="primary" />,
                }}
              />
              <PasswordInput setPassword={setPassword} margin="normal" autoComplete="new-password" min="6" max="32" />
              {codeRequired && (
                <TextField
                  margin="normal"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  fullWidth
                  label="Authenticator Code"
                  autoFocus
                  InputProps={{
                    endAdornment: <KeyIcon color="primary" />,
                  }}
                  inputProps={{ maxLength: 6 }}
                />
              )}
              <Box display={"flex"} justifyContent={"flex-end"}>
                <Link
                  data-id="authForgotPassword"
                  style={{
                    textAlign: "right",
                    textDecoration: "underline",
                  }}
                  to="/forgot-password"
                >
                  Forgot Password?
                </Link>
              </Box>
              <Button
                data-id="authSignInButton"
                color="secondary"
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 2, mb: 2 }}
                disabled={!(loginId && password && (!codeRequired || (codeRequired && code))) || loading}
              >
                {loading ? <CircularProgress style={{ width: "20px", height: "20px" }} /> : "Sign In"}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
